/**
 * HeaderAskAdaController
 *
 * Controls the unified Ask Ada input in the header.
 * This provides a single, consistent Ask Ada experience across all pages.
 *
 * Features:
 * - Always visible in the header
 * - Uses UnifiedAdaContextService for consistent context
 * - Keyboard shortcut (Ctrl+K / Cmd+K) to focus
 * - Expandable response panel
 * - Link to full Ask Ada tab for extended conversations
 */

import { unifiedAdaContext } from '../../services/unified-ada-context';
import { adaAgenticChat } from '../../services/ada-tools';
import { isMainnet } from '../../config/environment';

export class HeaderAskAdaController {
    private static instance: HeaderAskAdaController | null = null;

    private input: HTMLInputElement | null = null;
    private sendBtn: HTMLButtonElement | null = null;
    private clearBtn: HTMLButtonElement | null = null;
    private responseDiv: HTMLElement | null = null;
    private closeBtn: HTMLElement | null = null;
    private isInitialized: boolean = false;

    // Store last conversation for transfer to full Ask Ada
    private lastQuestion: string = '';
    private lastAnswer: string = '';

    /**
     * Singleton pattern - ensures only one instance exists
     */
    public static getInstance(): HeaderAskAdaController {
        if (!HeaderAskAdaController.instance) {
            HeaderAskAdaController.instance = new HeaderAskAdaController();
        }
        return HeaderAskAdaController.instance;
    }

    private constructor() {
        console.log('HeaderAskAdaController: Created');
    }

    /**
     * Initialize the controller - call this after DOM is ready
     */
    public initialize(): void {
        if (this.isInitialized) {
            console.log('HeaderAskAdaController: Already initialized');
            return;
        }

        // Get DOM elements
        this.input = document.getElementById('header-ada-input') as HTMLInputElement;
        this.sendBtn = document.getElementById('header-ada-send') as HTMLButtonElement;
        this.clearBtn = document.getElementById('header-ada-clear') as HTMLButtonElement;
        this.responseDiv = document.getElementById('header-ada-response');
        this.closeBtn = document.getElementById('header-ada-close');

        if (!this.input || !this.sendBtn) {
            console.warn('HeaderAskAdaController: Header Ask Ada elements not found');
            return;
        }

        // Setup event listeners
        this.setupEventListeners();

        // Setup keyboard shortcut
        this.setupKeyboardShortcut();

        this.isInitialized = true;
        console.log('HeaderAskAdaController: Initialized successfully');
    }

    /**
     * Setup event listeners for the Ask Ada input
     */
    private setupEventListeners(): void {
        // Send on button click - stop propagation to prevent click-outside handler from closing
        this.sendBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.sendQuery();
        });

        // Send on Enter key
        this.input?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.sendQuery();
            }
        });

        // Show/hide clear button based on input content
        if (this.input) {
            this.input.addEventListener('input', () => {
                this.updateClearButtonVisibility();
            });
        }

        // Clear input on clear button click
        if (this.clearBtn) {
            this.clearBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.clearInput();
            });
        }

        // Close response panel - stop propagation
        this.closeBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.hideResponse();
        });

        // Prevent clicks inside response from closing it
        this.responseDiv?.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        // Prevent clicks on input from closing response
        this.input?.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        // Close response when clicking outside
        document.addEventListener('click', (e) => {
            if (this.responseDiv && !this.responseDiv.classList.contains('hidden')) {
                // Re-query elements to ensure fresh references
                const responseDiv = document.getElementById('header-ada-response');
                const input = document.getElementById('header-ada-input');
                const sendBtn = document.getElementById('header-ada-send');

                const target = e.target as HTMLElement;
                const isInsideResponse = responseDiv?.contains(target);
                const isInsideInput = input?.contains(target) || sendBtn?.contains(target);

                if (!isInsideResponse && !isInsideInput) {
                    this.hideResponse();
                }
            }
        });
    }

    /**
     * Setup keyboard shortcut (Ctrl+K / Cmd+K) to focus Ask Ada
     */
    private setupKeyboardShortcut(): void {
        document.addEventListener('keydown', (e) => {
            // Ctrl+K or Cmd+K
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                this.focus();
            }

            // Escape to close response and blur input
            if (e.key === 'Escape') {
                this.hideResponse();
                this.input?.blur();
            }
        });
    }

    /**
     * Focus the Ask Ada input
     */
    public focus(): void {
        this.input?.focus();
        this.input?.select();
    }

    /**
     * Update clear button visibility based on input content
     */
    private updateClearButtonVisibility(): void {
        const input = document.getElementById('header-ada-input') as HTMLInputElement;
        const clearBtn = document.getElementById('header-ada-clear');

        if (!input || !clearBtn) return;

        if (input.value.trim().length > 0) {
            clearBtn.classList.remove('hidden');
        } else {
            clearBtn.classList.add('hidden');
        }
    }

    /**
     * Clear the input field and hide clear button
     */
    private clearInput(): void {
        const input = document.getElementById('header-ada-input') as HTMLInputElement;
        const clearBtn = document.getElementById('header-ada-clear');

        if (input) {
            input.value = '';
            input.focus();
        }

        if (clearBtn) {
            clearBtn.classList.add('hidden');
        }
    }

    /**
     * Send query to Ada using agentic tool use
     * Ada can now query vector_db directly to find information
     */
    private async sendQuery(): Promise<void> {
        // Re-query elements to ensure fresh references
        const input = document.getElementById('header-ada-input') as HTMLInputElement;
        const responseText = document.getElementById('header-ada-response-text');
        const responseDiv = document.getElementById('header-ada-response');

        if (!input || !responseText || !responseDiv) {
            console.error('HeaderAskAdaController: Required elements not found', { input: !!input, responseText: !!responseText, responseDiv: !!responseDiv });
            return;
        }

        const query = input.value.trim();
        if (!query) return;

        console.log('HeaderAskAdaController: Sending query with tool use:', query);

        // Show loading state
        this.setLoadingState(true);
        this.showResponse();

        try {
            // Use agentic chat with tool use (mainnet only for now)
            if (isMainnet()) {
                console.log('HeaderAskAdaController: Using agentic chat with tool use');

                const result = await adaAgenticChat.chat(query);

                if (result.error) {
                    console.error('HeaderAskAdaController: Agentic chat error:', result.error);
                    // Fallback to context-based approach
                    await this.sendQueryWithContext(query);
                } else {
                    const toolInfo = result.toolsUsed.length > 0
                        ? `\n\n*Tools used: ${result.toolsUsed.join(', ')}*`
                        : '';
                    this.displayResponse(query, result.response + toolInfo);
                }
            } else {
                // Local development - use context-based approach via canister
                await this.sendQueryWithContext(query);
            }
        } catch (error) {
            console.error('HeaderAskAdaController: Query error:', error);
            this.displayResponse(query, this.getErrorResponse(query));
        }

        this.setLoadingState(false);
    }

    /**
     * Fallback: Send query using pre-built context (original approach)
     */
    private async sendQueryWithContext(query: string): Promise<void> {
        console.log('HeaderAskAdaController: Using context-based approach');

        const context = await unifiedAdaContext.buildFullContext();
        const canisterClient = (window as any).canisterClient;

        if (!canisterClient) {
            throw new Error('Canister client not available');
        }

        const systemPrompt = `You are Ada, an AI assistant for the Agentic Service Discovery (ASD) system.
You help users understand their enterprise infrastructure, business capabilities, platform assignments, and IT health status.
Provide concise, helpful responses based on the context data provided.
Focus on actionable insights and clear explanations.`;

        const fullContext = `${systemPrompt}\n\n${context}`;

        const chatRequest = {
            messages: [
                {
                    role: 'user',
                    content: `${query}\n\nPlease provide a concise, helpful response based on the context data provided.`
                }
            ],
            context: [fullContext],
            conversation_id: []
        };

        const result = await canisterClient.chatWithClaude(chatRequest);

        if ('Ok' in result) {
            const response = result.Ok.message
                .replace(/\\n/g, '\n')
                .replace(/\\'/g, "'");
            this.displayResponse(query, response);
        } else {
            this.displayResponse(query, this.getErrorResponse(query));
        }
    }

    /**
     * Display response in the response panel
     */
    private displayResponse(question: string, answer: string): void {
        // Re-query to ensure fresh reference
        const responseText = document.getElementById('header-ada-response-text');
        if (!responseText) {
            console.error('HeaderAskAdaController: Response text element not found!');
            return;
        }

        console.log('HeaderAskAdaController: Displaying response');

        // Store for potential transfer to full conversation
        this.lastQuestion = question;
        this.lastAnswer = answer;

        const formattedAnswer = this.formatMarkdown(answer);
        responseText.innerHTML = `
            <div class="mb-3 pb-3 border-b border-purple-100">
                <div class="text-xs text-purple-600 font-medium mb-1">Your question:</div>
                <div class="text-sm text-gray-800 italic">"${this.escapeHtml(question)}"</div>
            </div>
            <div>
                <div class="text-xs text-purple-600 font-medium mb-1">Ada:</div>
                <div class="prose prose-sm max-w-none">${formattedAnswer}</div>
            </div>
        `;
    }

    /**
     * Open full conversation in Ask Ada tab
     * Transfers the current question and answer to the full chat interface
     */
    public openFullConversation(): void {
        // Hide the response panel
        this.hideResponse();

        const question = this.lastQuestion;
        const answer = this.lastAnswer;

        if (!question || !answer) {
            console.warn('HeaderAskAdaController: No conversation to transfer');
            return;
        }

        // Store conversation in sessionStorage as backup for fresh initialization
        sessionStorage.setItem('headerAdaConversation', JSON.stringify({
            question: question,
            answer: answer,
            timestamp: Date.now()
        }));
        console.log('HeaderAskAdaController: Stored conversation for transfer');

        // Navigate to Ask Ada page
        const pageManagement = (window as any).pageManagement;
        if (pageManagement) {
            pageManagement.showPage('ii-agent');
        }

        // After navigation, directly call loadConversationFromHeader on ii-agent-ui
        // Use timeout to ensure page has switched and UI is ready
        setTimeout(() => {
            const iiAgentUI = (window as any).iiAgentUI;
            if (iiAgentUI && typeof iiAgentUI.loadConversationFromHeader === 'function') {
                console.log('HeaderAskAdaController: Directly loading conversation into ii-agent');
                iiAgentUI.loadConversationFromHeader(question, answer);
                // Clear sessionStorage since we loaded directly
                sessionStorage.removeItem('headerAdaConversation');
            } else {
                console.warn('HeaderAskAdaController: iiAgentUI not available, relying on sessionStorage');
            }
        }, 300);
    }

    /**
     * Show the response panel
     */
    private showResponse(): void {
        // Re-query to ensure fresh reference
        const responseDiv = document.getElementById('header-ada-response');
        if (responseDiv) {
            responseDiv.classList.remove('hidden');
            console.log('HeaderAskAdaController: Response panel shown');
        } else {
            console.error('HeaderAskAdaController: Response div not found!');
        }
    }

    /**
     * Hide the response panel
     */
    private hideResponse(): void {
        // Re-query to ensure fresh reference
        const responseDiv = document.getElementById('header-ada-response');
        if (responseDiv) {
            responseDiv.classList.add('hidden');
        }
    }

    /**
     * Set loading state on the send button
     */
    private setLoadingState(loading: boolean): void {
        // Re-query to ensure fresh references
        const sendBtn = document.getElementById('header-ada-send') as HTMLButtonElement;
        const responseText = document.getElementById('header-ada-response-text');

        if (!sendBtn) return;

        if (loading) {
            sendBtn.disabled = true;
            sendBtn.innerHTML = `
                <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
                </svg>
                <span class="hidden sm:inline">...</span>
            `;

            // Show loading message in response
            if (responseText) {
                responseText.innerHTML = `
                    <div class="flex items-center gap-3 text-gray-500">
                        <svg class="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
                            <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
                        </svg>
                        <span>Ada is thinking...</span>
                    </div>
                `;
            }
        } else {
            sendBtn.disabled = false;
            sendBtn.innerHTML = `
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="hidden sm:inline">Ask Ada</span>
            `;
        }
    }

    /**
     * Format markdown in response
     */
    private formatMarkdown(text: string): string {
        return text
            // Headings
            .replace(/^#### (.+)$/gm, '<h4 class="text-sm font-semibold text-gray-800 mt-3 mb-1">$1</h4>')
            .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold text-gray-800 mt-3 mb-1">$1</h3>')
            .replace(/^## (.+)$/gm, '<h2 class="text-lg font-semibold text-gray-800 mt-3 mb-2">$1</h2>')
            .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold text-gray-800 mt-3 mb-2">$1</h1>')
            // Bold and italic
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            // Code
            .replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 rounded text-sm">$1</code>')
            // Lists (unordered)
            .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
            .replace(/(<li.*<\/li>\n?)+/g, '<ul class="list-disc my-2">$&</ul>')
            // Lists (ordered)
            .replace(/^\d+\. (.+)$/gm, '<li class="ml-4">$1</li>')
            // Newlines
            .replace(/\n/g, '<br>');
    }

    /**
     * Escape HTML entities
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Get error/fallback response
     */
    private getErrorResponse(query: string): string {
        return `I apologize, but I'm having trouble connecting to the AI service right now.

**Your question was:** "${query}"

Please try again in a moment, or navigate to the full **Ask Ada** tab for more detailed assistance.

**Quick tips:**
- Check that the proxy server is running
- Ensure you're logged in
- Try refreshing the page`;
    }
}

// Create and export singleton instance
export const headerAskAda = HeaderAskAdaController.getInstance();

// Also attach to window for global access
declare global {
    interface Window {
        headerAskAda: HeaderAskAdaController;
    }
}

window.headerAskAda = headerAskAda;

console.log('HeaderAskAdaController: Module loaded');
